import {
  createEmptyAnamnesisSections,
  getAnamnesisSections,
  getClinicalHistoryType,
  hasAnamnesisContent,
  isClinicalHistoryRecord,
  isInitialAnamnesisRecord,
  isStructuredAnamnesisRecord,
} from '../../../utils/clinicalDocuments';
import {
  getMedicalRecordAttachmentsObject,
  isMedicalRecordInvalidated,
} from '../../../utils/medicalRecordStatus';
import type { InitialAssessmentAnamnesisSnapshot } from './initialAssessmentDomain';

export interface InitialAssessmentAnamnesisSourceRecord {
  recordId: string;
  recordDate: string;
  recordTitle?: string | null;
  recordDescription?: string | null;
  recordType?: string | null;
  recordAttachments?: unknown;
}

export interface InitialAssessmentAnamnesisOption {
  snapshot: InitialAssessmentAnamnesisSnapshot;
  label: string;
}

const toPlainText = (value: string): string => {
  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = value;
    return (container.textContent || container.innerText || '').trim();
  }

  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getDocumentVersion = (recordAttachments: unknown): number => {
  const attachments = getMedicalRecordAttachmentsObject(recordAttachments);
  const version = Number(attachments.clinicalDocumentVersion);
  return Number.isInteger(version) && version > 0 ? version : 1;
};

const formatOptionDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

export const buildInitialAssessmentAnamnesisOption = (
  record: InitialAssessmentAnamnesisSourceRecord,
): InitialAssessmentAnamnesisOption | null => {
  if (
    !record.recordId ||
    !record.recordDate ||
    !Number.isFinite(Date.parse(record.recordDate)) ||
    isMedicalRecordInvalidated(record.recordType, record.recordAttachments)
  ) {
    return null;
  }

  const isStructured = isStructuredAnamnesisRecord(record.recordAttachments);
  const isInitial = isInitialAnamnesisRecord(record.recordAttachments);
  const clinicalHistoryType = getClinicalHistoryType(record.recordAttachments);
  const isClinicalHistoryAnamnesis =
    isClinicalHistoryRecord(record.recordAttachments) &&
    (clinicalHistoryType === 'initial_anamnesis' ||
      clinicalHistoryType === 'anamnesis' ||
      clinicalHistoryType === 'anamnesis_update' ||
      clinicalHistoryType === 'hma');
  if (!isStructured && !isInitial && !isClinicalHistoryAnamnesis) return null;

  const sections = getAnamnesisSections(record.recordAttachments);
  if (!hasAnamnesisContent(sections)) {
    const legacyDescription = toPlainText(record.recordDescription || '');
    if (legacyDescription) {
      Object.assign(sections, {
        ...createEmptyAnamnesisSections(),
        ...(isInitial || clinicalHistoryType === 'initial_anamnesis' || clinicalHistoryType === 'hma'
          ? { currentIllnessHistory: legacyDescription }
          : { additionalNotes: legacyDescription }),
      });
    }
  }
  if (!hasAnamnesisContent(sections)) return null;

  const documentVersion = getDocumentVersion(record.recordAttachments);
  const sourceRecordTitle = record.recordTitle?.trim() || (isStructured
    ? 'Anamnese'
    : isInitial || clinicalHistoryType === 'initial_anamnesis' || clinicalHistoryType === 'hma'
      ? 'Anamnese inicial (HMA)'
      : 'Anamnese');
  const snapshot: InitialAssessmentAnamnesisSnapshot = {
    sourceRecordId: record.recordId,
    sourceRecordDate: record.recordDate,
    sourceRecordTitle,
    sourceKind: isStructured
      ? 'structured_anamnesis'
      : isInitial
        ? 'initial_anamnesis'
        : 'clinical_history_anamnesis',
    documentVersion,
    sections,
  };

  return {
    snapshot,
    label: `${sourceRecordTitle} • versão ${documentVersion} • ${formatOptionDate(record.recordDate)}`,
  };
};

export const buildInitialAssessmentAnamnesisOptions = (
  records: InitialAssessmentAnamnesisSourceRecord[],
): InitialAssessmentAnamnesisOption[] =>
  records
    .map(buildInitialAssessmentAnamnesisOption)
    .filter((option): option is InitialAssessmentAnamnesisOption => Boolean(option))
    .sort((left, right) => {
      const versionDelta = right.snapshot.documentVersion - left.snapshot.documentVersion;
      if (versionDelta !== 0) return versionDelta;
      return new Date(right.snapshot.sourceRecordDate).getTime() - new Date(left.snapshot.sourceRecordDate).getTime();
    });
